import { environment } from '@env/environment';

import {
  ORG_HOME_FALLBACK_DEFAULT_MODE,
  ORG_HOME_FALLBACK_MODES,
  OrgContext,
  OrgEntitlement,
  OrgHome,
  OrgHomeMode,
  OrgKind,
  OrgMember,
  OrgMembership,
  Organization,
  orgProductLine,
  OrgRole,
  OrgStage,
} from '../../domain/models/organization.model';
import {
  EMPTY_ORG_WHEEL,
  OrgWheel,
  OrgWheelAction,
  OrgWheelActionTarget,
  OrgWheelBand,
  OrgWheelDimension,
  OrgWheelScaleOption,
} from '../../domain/models/org-wheel.model';
import {
  EMPTY_ORG_ENGAGEMENT,
  OrgBadge,
  OrgBadgeState,
  OrgChallenge,
  OrgEngagement,
  OrgEngagementOverview,
  OrgLeaderboardRow,
  OrgProgressResult,
  OrgPulse,
  OrgPulseOption,
  OrgPulseQuestion,
  OrgStreak,
} from '../../domain/models/org-engagement.model';
import {
  EMPTY_HEALTH_CATALOG,
  EMPTY_HEALTH_SYNC_RESULT,
  OrgHealthCatalog,
  OrgHealthMetric,
  OrgHealthRejection,
  OrgHealthSyncResult,
} from '../../domain/models/org-health.model';
import {
  EMPTY_COUNSELING_OPTIONS,
  OrgBookingResult,
  OrgCounselingCost,
  OrgCounselingOptions,
  OrgCounselingSession,
  OrgCounselingSlot,
  OrgCounselor,
  OrgGuardianConsentPolicy,
  OrgGuardianDecisionResult,
  OrgGuardianRequest,
} from '../../domain/models/org-counseling.model';
import {
  OrgChild,
  OrgChildDigest,
  OrgConsentDecision,
  OrgConsentRequest,
} from '../../domain/models/org-guardian.model';
import {
  OrgContextDto,
  OrgEntitlementDto,
  OrgHomeDto,
  OrgHomeModeDto,
  OrgMemberDto,
  OrgMembershipDto,
  OrgOrganizationDto,
} from '../api/dtos/responses/org-context-dto-response';
import {
  OrgWheelActionTargetDto,
  OrgWheelDimensionDto,
  OrgWheelDto,
  OrgWheelScaleOptionDto,
} from '../api/dtos/responses/org-wheel-dto-response';
import {
  OrgBadgeDto,
  OrgChallengeDto,
  OrgEngagementDto,
  OrgEngagementOverviewDto,
  OrgLeaderboardRowDto,
  OrgProgressResultDto,
  OrgPulseDto,
  OrgPulseOptionDto,
  OrgPulseQuestionDto,
  OrgStreakDto,
} from '../api/dtos/responses/org-engagement-dto-response';
import {
  OrgHealthCatalogDto,
  OrgHealthMetricDto,
  OrgHealthRejectionDto,
  OrgHealthSyncResultDto,
} from '../api/dtos/responses/org-health-dto-response';
import {
  OrgBookingResultDto,
  OrgCounselingCostDto,
  OrgCounselingOptionsDto,
  OrgCounselingSessionDto,
  OrgCounselingSlotDto,
  OrgCounselorDto,
  OrgGuardianDecisionResultDto,
  OrgGuardianPolicyDto,
  OrgGuardianRequestDto,
} from '../api/dtos/responses/org-counseling-dto-response';
import {
  OrgChildDigestDto,
  OrgChildDto,
  OrgConsentDecisionDto,
  OrgConsentRequestDto,
} from '../api/dtos/responses/org-guardian-dto-response';

/**
 * DTO → domain mapping for every B2B payload.
 *
 * Pure functions, no DI: the repository calls them and the specs exercise them
 * directly. The house style is "honest zero, never a fabricated value" — a
 * missing number becomes 0, a missing string becomes `''` or `null` per the
 * field's own meaning, and nothing is invented to fill a gap.
 *
 * Server-authored COPY (`name`, `question`, `band_label`, `action_label`,
 * `source_label`, `status_label`, `tier_note`, refusal sentences …) passes
 * through UNTOUCHED — §1.5: it is contractual wording from signed decks and must
 * never be re-translated or re-derived from its key in the client.
 */

// ── shared coercions ─────────────────────────────────────────────────────────

/** A finite number, else the fallback. Tolerates the numeric strings some rows carry. */
function num(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return fallback;
}

/** A finite number or null — for fields where 0 and "absent" mean different things. */
function numOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = num(value, Number.NaN);
  return Number.isFinite(n) ? n : null;
}

/** A non-empty trimmed string, else the fallback. */
function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() !== '' ? value : fallback;
}

/** A non-empty trimmed string, else null. */
function strOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function bool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (value === '1' || value === 'true') return true;
  if (value === '0' || value === 'false') return false;
  return fallback;
}

/** A readonly array, tolerant of null/absent/non-array. */
function list<T>(value: readonly T[] | null | undefined): readonly T[] {
  return Array.isArray(value) ? value : [];
}

// ── §2 context ───────────────────────────────────────────────────────────────

const ORG_KINDS: ReadonlySet<string> = new Set<OrgKind>(['university', 'school', 'company']);
const ORG_ROLES: ReadonlySet<string> = new Set<OrgRole>([
  'member',
  'org_admin',
  'counselor',
  'teacher',
  'guardian',
]);
const ORG_STAGES: ReadonlySet<string> = new Set<OrgStage>(['kindergarten', 'primary', 'middle', 'high']);

/**
 * Trap 18: `organization.logo` is a RAW STORAGE PATH (`organizations/qu-logo.png`),
 * not a URL — "prefix it yourself".
 *
 * Laravel's public disk is served from `/storage/<path>` on the API host, which
 * is the only host the client knows about, so that is what we build. An absolute
 * URL is passed through untouched in case the backend ever starts sending one,
 * and a missing logo stays null rather than becoming a broken `<img>`.
 */
export function orgLogoUrl(path: string | null | undefined): string | null {
  const raw = strOrNull(path);
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = environment.apiUrl.replace(/\/+$/, '');
  return `${base}/storage/${raw.replace(/^\/+/, '')}`;
}

function toOrganization(dto: OrgOrganizationDto | null | undefined): Organization {
  const kind = str(dto?.kind);
  const logoPath = strOrNull(dto?.logo);
  return {
    id: num(dto?.id),
    kind: ORG_KINDS.has(kind) ? (kind as OrgKind) : 'company',
    name: str(dto?.name),
    logoPath,
    logoUrl: orgLogoUrl(logoPath),
    badge: str(dto?.badge),
    privacyLine: strOrNull(dto?.privacy_line),
  };
}

function toMembership(dto: OrgMembershipDto | null | undefined): OrgMembership {
  const role = str(dto?.role);
  const stage = str(dto?.stage);
  return {
    memberCode: str(dto?.member_code),
    role: ORG_ROLES.has(role) ? (role as OrgRole) : 'member',
    unit: strOrNull(dto?.unit),
    stage: ORG_STAGES.has(stage) ? (stage as OrgStage) : null,
  };
}

/**
 * `active` is always present; when false the block carries NOTHING else, so the
 * other three are read as null rather than defaulted to a figure that would
 * render as real coverage.
 */
function toEntitlement(dto: OrgEntitlementDto | null | undefined): OrgEntitlement {
  const active = bool(dto?.active);
  if (!active) return { active: false, label: null, endsAt: null, daysLeft: null };
  return {
    active: true,
    label: strOrNull(dto?.label),
    // null here is an OPEN-ENDED gift, not a missing value (§2).
    endsAt: strOrNull(dto?.ends_at),
    daysLeft: numOrNull(dto?.days_left),
  };
}

function toHomeMode(dto: OrgHomeModeDto): OrgHomeMode {
  return { key: str(dto.key), label: str(dto.label) };
}

/**
 * The segmented switch, IN THE ORDER GIVEN (§2). Modes with no key are dropped —
 * an unlabelled tab would render as a blank segment the member cannot name.
 *
 * A list the server actually sent is used exactly as sent, however short: an org
 * that bought only `start` is a settings decision, not a gap to paper over. The
 * fallback fires only when there is NOTHING to honour — no `home` block, or one
 * whose modes are all keyless — because a member with an empty switch would get
 * a hub with no tabs and a blank product line. Same reasoning for
 * `product_line`, which §2 derives from the org's own kind.
 */
function toHome(dto: OrgHomeDto | null | undefined, organization: Organization): OrgHome {
  const sent = list(dto?.modes).map(toHomeMode).filter((m) => m.key !== '');
  const modes = sent.length > 0 ? sent : ORG_HOME_FALLBACK_MODES;
  return {
    productLine: str(dto?.product_line, orgProductLine(organization.kind, organization.name)),
    modes,
    // Honour the server's default rather than hardcoding 'start'; fall back to
    // the first mode it actually sent, and only then to the documented constant.
    defaultMode: str(dto?.default_mode, modes[0]?.key ?? ORG_HOME_FALLBACK_DEFAULT_MODE),
  };
}

function toMember(dto: OrgMemberDto): OrgMember {
  // Mapped first: `home`'s fallbacks are derived from the org's kind and name.
  const organization = toOrganization(dto.organization);
  return {
    organization,
    membership: toMembership(dto.membership),
    entitlement: toEntitlement(dto.entitlement),
    escalationTarget: strOrNull(dto.escalation?.target),
    home: toHome(dto.home, organization),
  };
}

/**
 * `data.member`, NOT flat `data` (trap 1). `member: null` is the ordinary
 * non-member case and maps to `null` — the signal every B2B surface reads.
 */
export function toOrgContext(dto: OrgContextDto | null | undefined): OrgContext {
  const member = dto?.member;
  return member ? toMember(member) : null;
}

// ── §3 the wheel ─────────────────────────────────────────────────────────────

const WHEEL_BANDS: ReadonlySet<string> = new Set<OrgWheelBand>(['balanced', 'acceptable', 'needs_care']);
const WHEEL_ACTIONS: ReadonlySet<string> = new Set<OrgWheelAction>([
  'khoyek',
  'programs',
  'article',
  'seminar',
  'care',
]);

function toActionTarget(dto: OrgWheelActionTargetDto | null | undefined): OrgWheelActionTarget {
  const type = str(dto?.type);
  return {
    type: type === 'programme' || type === 'article' || type === 'seminar' ? type : null,
    id: numOrNull(dto?.id),
    title: strOrNull(dto?.title),
    exists: bool(dto?.exists),
  };
}

/**
 * One dimension. Copy fields pass through as sent (§3: "never map keys to your
 * own strings"), and `action` falls back to `khoyek` — the same destination the
 * server's own dead-end fallback demotes to — so an unrecognised action can never
 * strand the CTA on nothing.
 */
function toWheelDimension(dto: OrgWheelDimensionDto): OrgWheelDimension {
  const band = str(dto.band);
  const action = str(dto.action);
  return {
    key: str(dto.key),
    isCore: bool(dto.is_core),
    name: str(dto.name),
    question: str(dto.question),
    meaning: str(dto.meaning),
    icon: str(dto.icon),
    hue: str(dto.hue),
    score: numOrNull(dto.score),
    band: WHEEL_BANDS.has(band) ? (band as OrgWheelBand) : null,
    bandLabel: strOrNull(dto.band_label),
    action: WHEEL_ACTIONS.has(action) ? (action as OrgWheelAction) : 'khoyek',
    actionLabel: str(dto.action_label),
    programmeKey: strOrNull(dto.programme_key),
    actionTarget: toActionTarget(dto.action_target),
  };
}

function toScaleOption(dto: OrgWheelScaleOptionDto): OrgWheelScaleOption {
  return { value: num(dto.value), label: str(dto.label) };
}

export function toOrgWheel(dto: OrgWheelDto | null | undefined): OrgWheel {
  if (!dto) return EMPTY_ORG_WHEEL;
  const dimensions = list(dto.dimensions).map(toWheelDimension).filter((d) => d.key !== '');
  return {
    // Trap 5: a SEPARATE slot, never folded into `dimensions`.
    core: dto.core ? toWheelDimension(dto.core) : null,
    // Already in the order this org wants — deliberately not re-sorted.
    dimensions,
    composite: numOrNull(dto.composite),
    answeredToday: num(dto.answered_today),
    // Trap 6: what THIS org shows. Falls back to what actually arrived rather
    // than the catalogue's 9, so the progress line can never exceed its total.
    total: num(dto.total, dimensions.length + (dto.core ? 1 : 0)),
    checkedInToday: bool(dto.checked_in_today),
    streak: num(dto.streak),
    crisis: bool(dto.crisis),
    scale: list(dto.scale).map(toScaleOption),
  };
}

// ── §4 engagement ────────────────────────────────────────────────────────────

const BADGE_STATES: ReadonlySet<string> = new Set<OrgBadgeState>(['earned', 'in_progress', 'locked']);

function toLeaderboardRow(dto: OrgLeaderboardRowDto): OrgLeaderboardRow {
  return {
    unitId: num(dto.unit_id),
    name: str(dto.name),
    value: num(dto.value),
    participants: num(dto.participants),
    isMine: bool(dto.is_mine),
    rank: num(dto.rank),
  };
}

export function toOrgChallenge(dto: OrgChallengeDto): OrgChallenge {
  return {
    id: num(dto.id),
    key: str(dto.key),
    title: str(dto.title),
    tagline: str(dto.tagline),
    metric: str(dto.metric),
    source: str(dto.source),
    sourceLabel: str(dto.source_label),
    selfReport: bool(dto.self_report),
    // §4.2: the input opens on `can_report`, never on `source` — so this is read
    // straight from the server and never re-derived from `self_report && joined`.
    canReport: bool(dto.can_report),
    maxDailyValue: numOrNull(dto.max_daily_value),
    target: num(dto.target),
    unitLabel: str(dto.unit_label),
    metricLabel: str(dto.metric_label),
    cadence: str(dto.cadence, 'daily'),
    progress: num(dto.progress),
    todayValue: num(dto.today_value),
    percent: num(dto.percent),
    daysLeft: num(dto.days_left),
    joined: bool(dto.joined),
    completed: bool(dto.completed),
    participants: num(dto.participants),
    myUnitRank: numOrNull(dto.my_unit_rank),
    leaderboard: list(dto.leaderboard).map(toLeaderboardRow),
    icon: str(dto.icon),
    hue: str(dto.hue),
  };
}

function toStreak(dto: OrgStreakDto | null | undefined): OrgStreak {
  return { days: num(dto?.days), label: str(dto?.label) };
}

function toBadge(dto: OrgBadgeDto): OrgBadge {
  const state = str(dto.state);
  return {
    key: str(dto.key),
    label: str(dto.label),
    state: BADGE_STATES.has(state) ? (state as OrgBadgeState) : 'locked',
    progress: num(dto.progress),
    target: num(dto.target),
    note: str(dto.note),
  };
}

/** The server names the label `label_ar`; `label` is tolerated as a future alias. */
function toPulseOption(dto: OrgPulseOptionDto): OrgPulseOption {
  return {
    value: num(dto.value),
    label: str(dto.label_ar, str(dto.label)),
    hue: str(dto.hue),
  };
}

function toPulseQuestion(dto: OrgPulseQuestionDto | null | undefined): OrgPulseQuestion | null {
  // NULL when the org has no active question, or for a non-member — checked
  // before the card is drawn rather than rendered as an empty question.
  if (!dto) return null;
  return {
    id: num(dto.id),
    text: str(dto.text),
    options: list(dto.options).map(toPulseOption),
    myValue: numOrNull(dto.my_value),
    note: str(dto.note),
  };
}

export function toOrgPulse(dto: OrgPulseDto | null | undefined): OrgPulse {
  return {
    // Always present, even when there is no question.
    period: str(dto?.period),
    answered: bool(dto?.answered),
    question: toPulseQuestion(dto?.question),
  };
}

export function toOrgEngagement(dto: OrgEngagementDto | null | undefined): OrgEngagement {
  if (!dto) return EMPTY_ORG_ENGAGEMENT;
  return {
    challenges: list(dto.challenges).map(toOrgChallenge),
    streak: toStreak(dto.streak),
    badges: list(dto.badges).map(toBadge),
    pulse: toOrgPulse(dto.pulse),
  };
}

/**
 * Trap 8: the PARTIAL shape `join` answers with. Deliberately mapped to its own
 * type — `streak_days` is a bare int here, and badges/pulse are simply absent,
 * so feeding this into {@link toOrgEngagement} would blank both.
 */
export function toOrgEngagementOverview(dto: OrgEngagementOverviewDto | null | undefined): OrgEngagementOverview {
  return {
    unitId: numOrNull(dto?.unit_id),
    streakDays: num(dto?.streak_days),
    challenges: list(dto?.challenges).map(toOrgChallenge),
  };
}

export function toOrgProgressResult(dto: OrgProgressResultDto | null | undefined): OrgProgressResult {
  return {
    value: num(dto?.value),
    challenge: dto?.challenge ? toOrgChallenge(dto.challenge) : null,
    overview: toOrgEngagementOverview(dto?.overview),
  };
}

// ── §5 health ────────────────────────────────────────────────────────────────

function toHealthMetric(dto: OrgHealthMetricDto): OrgHealthMetric {
  return { key: str(dto.key), unit: str(dto.unit), label: str(dto.label) };
}

export function toHealthCatalog(dto: OrgHealthCatalogDto | null | undefined): OrgHealthCatalog {
  if (!dto) return EMPTY_HEALTH_CATALOG;
  return {
    metrics: list(dto.metrics).map(toHealthMetric).filter((m) => m.key !== ''),
    sources: list(dto.sources).filter((s): s is string => typeof s === 'string'),
    maxBackfillDays: num(dto.max_backfill_days, EMPTY_HEALTH_CATALOG.maxBackfillDays),
    maxBatch: num(dto.max_batch, EMPTY_HEALTH_CATALOG.maxBatch),
    privacy: str(dto.privacy),
  };
}

function toHealthRejection(dto: OrgHealthRejectionDto): OrgHealthRejection {
  return { index: num(dto.index), reason: str(dto.reason) };
}

/** Trap 11: `accepted: 0` is a perfectly ordinary 200 — the caller must read this. */
export function toHealthSyncResult(dto: OrgHealthSyncResultDto | null | undefined): OrgHealthSyncResult {
  if (!dto) return EMPTY_HEALTH_SYNC_RESULT;
  return {
    accepted: num(dto.accepted),
    rejected: list(dto.rejected).map(toHealthRejection),
    creditedMetrics: list(dto.credited_metrics).filter((m): m is string => typeof m === 'string'),
  };
}

// ── §7 counseling ────────────────────────────────────────────────────────────

function toCounselor(dto: OrgCounselorDto | null | undefined): OrgCounselor | null {
  if (!dto) return null;
  const name = str(dto.name);
  if (!name) return null;
  return { memberId: num(dto.member_id), name, title: strOrNull(dto.title) };
}

function toSlot(dto: OrgCounselingSlotDto): OrgCounselingSlot {
  return {
    slotId: num(dto.slot_id),
    date: str(dto.date),
    weekday: str(dto.weekday),
    timeLabel: str(dto.time_label),
    startTime: str(dto.start_time),
    duration: num(dto.duration),
    location: str(dto.location),
    locationLabel: str(dto.location_label),
    counselor: toCounselor(dto.counselor),
  };
}

function toGuardianPolicy(dto: OrgGuardianPolicyDto | null | undefined): OrgGuardianConsentPolicy | null {
  // ABSENT (not null-valued) in the proxy-stage shape — so absence is preserved
  // as null and `hasBookingShape()` can tell the two payloads apart.
  if (!dto) return null;
  return { consentRequired: bool(dto.consent_required), note: str(dto.note) };
}

function toCost(dto: OrgCounselingCostDto | null | undefined): OrgCounselingCost | null {
  if (!dto) return null;
  return { free: bool(dto.free, true), label: str(dto.label) };
}

/**
 * Trap 12: TWO SHAPES. The proxy-stage payload carries no `slots`, `guardian`,
 * `cost` or `checklist` at all; the `no_slots` payload has `slots: []` but keeps
 * the other three. Mapping absence to null/[] rather than to a default object is
 * what lets `hasBookingShape()` / `isProxyStage()` distinguish them downstream.
 */
export function toCounselingOptions(dto: OrgCounselingOptionsDto | null | undefined): OrgCounselingOptions {
  if (!dto) return EMPTY_COUNSELING_OPTIONS;
  return {
    available: bool(dto.available),
    reason: strOrNull(dto.reason),
    message: strOrNull(dto.message),
    counselors: list(dto.counselors)
      .map(toCounselor)
      .filter((c): c is OrgCounselor => c !== null),
    slots: list(dto.slots).map(toSlot),
    guardian: toGuardianPolicy(dto.guardian),
    cost: toCost(dto.cost),
    checklist: list(dto.checklist).filter((c): c is string => typeof c === 'string' && c.trim() !== ''),
  };
}

export function toCounselingSession(dto: OrgCounselingSessionDto): OrgCounselingSession {
  return {
    id: num(dto.id),
    date: str(dto.date),
    timeLabel: str(dto.time_label),
    startTime: str(dto.start_time),
    location: str(dto.location),
    locationLabel: str(dto.location_label),
    status: str(dto.status),
    // Ready to render, straight from the server — never re-derived from `status`.
    statusLabel: str(dto.status_label),
    guardianState: str(dto.guardian_state, 'not_required'),
    counselor: strOrNull(dto.counselor),
    cancelReason: strOrNull(dto.cancel_reason),
  };
}

export function toCounselingSessions(
  dto: { sessions?: readonly OrgCounselingSessionDto[] | null } | null | undefined,
): readonly OrgCounselingSession[] {
  return list(dto?.sessions).map(toCounselingSession);
}

export function toBookingResult(dto: OrgBookingResultDto | null | undefined): OrgBookingResult {
  return {
    sessions: list(dto?.sessions).map(toCounselingSession),
    // «أرسلنا طلب الموافقة لولي أمرك…» / «تم تأكيد موعدك.» — shown verbatim (§7.2).
    message: strOrNull(dto?.message),
  };
}

export function toGuardianRequest(dto: OrgGuardianRequestDto): OrgGuardianRequest {
  return { ...toCounselingSession(dto), child: str(dto.child) };
}

export function toGuardianRequests(
  dto: { requests?: readonly OrgGuardianRequestDto[] | null } | null | undefined,
): readonly OrgGuardianRequest[] {
  return list(dto?.requests).map(toGuardianRequest);
}

export function toGuardianDecisionResult(
  dto: OrgGuardianDecisionResultDto | null | undefined,
): OrgGuardianDecisionResult {
  return {
    requests: list(dto?.requests).map(toGuardianRequest),
    message: strOrNull(dto?.message),
  };
}

// ── §8 guardian ──────────────────────────────────────────────────────────────

export function toChild(dto: OrgChildDto): OrgChild {
  return {
    // An ORG MEMBER id (trap 14) — this is the digest path id, not a user id.
    memberId: num(dto.member_id),
    name: str(dto.name),
    cohort: strOrNull(dto.cohort),
    stage: strOrNull(dto.stage),
    relationship: strOrNull(dto.relationship),
    tier: str(dto.tier),
    tierNote: str(dto.tier_note),
    sections: list(dto.sections).filter((s): s is string => typeof s === 'string'),
  };
}

export function toChildren(dto: { list?: readonly OrgChildDto[] | null } | null | undefined): readonly OrgChild[] {
  // `{list: []}` with a 200 is the honest "not a guardian" answer (§8.1).
  return list(dto?.list).map(toChild);
}

export function toChildDigest(dto: OrgChildDigestDto | null | undefined): OrgChildDigest {
  const sections = dto?.sections;
  return {
    child: {
      memberId: num(dto?.child?.member_id),
      name: str(dto?.child?.name),
      cohort: strOrNull(dto?.child?.cohort),
      stage: strOrNull(dto?.child?.stage),
      relationship: strOrNull(dto?.child?.relationship),
    },
    tier: str(dto?.tier),
    tierNote: str(dto?.tier_note),
    // A SPARSE map (trap 15) — kept exactly as sent so `digestSections()` can tell
    // "allowed but empty" from "allowed and present".
    sections: sections && typeof sections === 'object' ? sections : {},
    availableSections: list(dto?.available_sections).filter((s): s is string => typeof s === 'string'),
  };
}

export function toConsentRequest(dto: OrgConsentRequestDto): OrgConsentRequest {
  return {
    referralId: num(dto.referral_id),
    childName: str(dto.child_name),
    caseType: strOrNull(dto.case_type),
    priority: strOrNull(dto.priority),
    askedAt: strOrNull(dto.asked_at),
    // Shown on the decision screen — consent with no stated scope is not
    // informed consent (§8.3).
    scope: list(dto.scope).filter((s): s is string => typeof s === 'string'),
    note: str(dto.note),
  };
}

export function toConsents(
  dto: { list?: readonly OrgConsentRequestDto[] | null } | null | undefined,
): readonly OrgConsentRequest[] {
  return list(dto?.list).map(toConsentRequest);
}

export function toConsentDecision(dto: OrgConsentDecisionDto | null | undefined): OrgConsentDecision {
  return {
    referralId: num(dto?.referral_id),
    guardianState: str(dto?.guardian_state),
  };
}
